import { useSelector, useDispatch } from "react-redux";
import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";

import { getRBDPerformanceMetrics } from "../../redux/slice/rbd-performance-metrics";
import {
  fetchKpiMyPerformance,
  fetchKpiNationalRegional,
  fetchKpiPendingReferrals,
  fetchKpiHcpGrowers,
  fetchKpiInsights,
  fetchKpiReachFrequency,
  fetchKpiTreatmentRate,
  fetchKpiTopYutrepiaWriters,
  fetchKpiHcpHcoAccountChanges,
  fetchKpiDeNovo,
  fetchKpiTyvasoDpiDosageDistribution,
  fetchKpiTyvasoDpiDosageNotification,
  fetchKpiReportSummary,
} from "../../redux/slice/kpi-widget-metrics";
import { getLeadershipMetrics } from "../../redux/slice/leadership-metrics";
import { setSelectedProduct } from "../../redux/slice/get-rep-emails";

import { LEADERSHIP_PRODUCT_LIST, PRODUCT_SALES_GROUP_MAP } from "../../utils/constantFiles/constant";
import {
  getSelectedMedicineLeadership,
  getLeadershipSalesGroup,
  withDoctorNamePrefix,
  insightRowMatchesSelectedProduct,
  distinctInsightDetailsCount,
  extractHcpNameFromDeNovoDescription,
  buildProductGroupFilterOptions,
  getSalesRepProductMetricsFromApi,
  getSalesRepPerformanceRowsByProduct,
  getSalesRepHcpGrowers,
  getPendingReferralsTotal,
  getPendingReferralsDetailRows,
  getPendingReferralsHcpRows,
} from "./myPerformanceHelpers";
import { buildMyPerformanceWidgetProps } from "./buildMyPerformanceWidgetProps";
import { useDisplayIdentity } from "../../hooks/useDisplayIdentity";

export function useMyPerformanceController({ isActive = true } = {}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  // Read at click time only. As a render dependency, every My Performance /
  // RepAssist toggle (which rewrites `?tab=`) rebuilt every widget's props and
  // re-rendered -- and re-animated -- the whole dashboard, hidden or not.
  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;
  const getLocationSearch = useCallback(() => locationSearchRef.current, []);

  const authUser = useSelector((state) => state.auth.user);
  const token = "__cookie__";
  const role = authUser?.business_function || "";
  const emailFromUrl = (authUser?.email || "")
    .trim()
    .toLowerCase();

  const {
    firstName: displayFirstName,
    isLoading: displayIdentityLoading,
  } = useDisplayIdentity();

  const kpiMyPerformance = useSelector(
    (state) => state.kpiWidgetMetrics.myPerformance,
  );
  const kpiNationalRegional = useSelector(
    (state) => state.kpiWidgetMetrics.nationalRegional,
  );
  const kpiPendingReferrals = useSelector(
    (state) => state.kpiWidgetMetrics.pendingReferrals,
  );
  const kpiHcpGrowers = useSelector(
    (state) => state.kpiWidgetMetrics.hcpGrowers,
  );
  const kpiInsights = useSelector((state) => state.kpiWidgetMetrics.insights);
  const kpiReachFrequency = useSelector(
    (state) => state.kpiWidgetMetrics.reachFrequency,
  );
  const kpiTreatmentRate = useSelector(
    (state) => state.kpiWidgetMetrics.treatmentRate,
  );
  const kpiTopYutrepiaWriters = useSelector(
    (state) => state.kpiWidgetMetrics.topYutrepiaWriters,
  );
  const kpiHcpHcoAccountChanges = useSelector(
    (state) => state.kpiWidgetMetrics.hcpHcoAccountChanges,
  );
  const kpiDeNovo = useSelector((state) => state.kpiWidgetMetrics.deNovo);
  const kpiTyvasoDpiDosageDistribution = useSelector(
    (state) => state.kpiWidgetMetrics.tyvasoDpiDosageDistribution,
  );
  const kpiTyvasoDpiDosageNotification = useSelector(
    (state) => state.kpiWidgetMetrics.tyvasoDpiDosageNotification,
  );
  // 6X6 / 3X3 National Performance report summary — LEADERSHIP-only in
  // Phase 1; RBD/SALESREP receive a `pending_phase_2` payload the widget
  // renders as a coming-soon card without erroring.
  const kpiReportSummary = useSelector(
    (state) => state.kpiWidgetMetrics.reportSummary,
  );

  const rbdMetrics = useSelector(
    (state) => state.rbdPerformanceMetrics.rbdMetrics,
  );

  const salesrepMetrics = useMemo(() => {
    const mp = kpiMyPerformance?.data;
    const nr = kpiNationalRegional?.data;
    const pr = kpiPendingReferrals?.data;
    const hg = kpiHcpGrowers?.data;
    const ins = kpiInsights?.data;
    const rf = kpiReachFrequency?.data;
    const tyw = kpiTopYutrepiaWriters?.data;
    const hhac = kpiHcpHcoAccountChanges?.data;
    return {
      ...(mp || {}),
      ...(nr && {
        national_performance: nr.national_performance,
        regional_performance: nr.regional_performance,
      }),
      ...(pr && {
        total_pending_referrals: pr.total_pending_referrals,
        pending_referrals_detail: pr.pending_referrals_detail,
        hcp_pending_referrals_count: pr.hcp_pending_referrals_count,
      }),
      ...(hg && { hcp_growers: hg.hcp_growers }),
      ...(ins && {
        insightcategory_hcp_counts: ins.insightcategory_hcp_counts,
        patient_trends: ins.patient_trends,
        physician_behavior: ins.physician_behavior,
        ut_engagement: ins.ut_engagement,
        competitor_dynamics: ins.competitor_dynamics,
      }),
      ...(rf && { reach_frequency: rf.reach_frequency }),
      ...(tyw && { top_yutrepia_writers: tyw.top_yutrepia_writers }),
      ...(hhac && { hcp_hco_account_changes: hhac.hcp_hco_account_changes }),
    };
  }, [
    kpiMyPerformance?.data,
    kpiNationalRegional?.data,
    kpiPendingReferrals?.data,
    kpiHcpGrowers?.data,
    kpiInsights?.data,
    kpiReachFrequency?.data,
    kpiTopYutrepiaWriters?.data,
    kpiHcpHcoAccountChanges?.data,
  ]);
  const leadershipMetrics = useSelector(
    (state) => state.leadershipMetrics.metrics,
  );
  const isLeadershipLoading = useSelector(
    (state) => state.leadershipMetrics.isLoading,
  );
  const isLeadershipError = useSelector(
    (state) => state.leadershipMetrics.isError,
  );
  const isRBDLoading = useSelector(
    (state) => state.rbdPerformanceMetrics.isLoading,
  );
  const isRBDError = useSelector(
    (state) => state.rbdPerformanceMetrics.isError,
  );
  const isRepLoading = useSelector((state) => state.repEmails.isLoading);
  const repProductGroup = useSelector((state) => state.repEmails.productGroup);
  const selectedProduct = useSelector(
    (state) => state.repEmails.selectedProduct,
  );

  const isRBD = (role ?? "").trim().toUpperCase() === "RBD";
  const isLeadership = (role ?? "").trim().toUpperCase() === "LEADERSHIP";

  const handleExploreClick = useCallback(() => {
    const params = new URLSearchParams(locationSearchRef.current);
    params.set("tab", "repassist");
    navigate(
      { pathname: "/", search: params.toString() },
      {
        state: {
          prefillPrompt:
            "Suggest some high growth potential HCPs to engage with",
          prefillId: Date.now(),
        },
      },
    );
  }, [navigate]);

  const handleTerritoryExploreClick = useCallback(
    (rep) => {
      const params = new URLSearchParams(locationSearchRef.current);
      params.set("tab", "repassist");
      navigate(
        { pathname: "/", search: params.toString() },
        {
          state: {
            prefillPrompt: `How is ${rep} tracking against their goals this quarter?`,
            prefillId: Date.now(),
          },
        },
      );
    },
    [navigate],
  );

  useEffect(() => {
    if (role) secureLocalStorage.setItem("businessFunction", role.toUpperCase());
    if (emailFromUrl) secureLocalStorage.setItem("userEmail", emailFromUrl);
  }, [role, emailFromUrl]);

  // Refs ensure each fetch fires at most once per session, so toggling between
  // the My Performance and RepAssist tabs never re-issues these calls.
  const ranRbdFetch = useRef(false);
  const ranSalesRepFetch = useRef(false);
  const ranLeadershipFetch = useRef(false);
  const ranTyvasoDpiFetch = useRef(false);
  const ranReportSummaryFetch = useRef(false);

  useEffect(() => {
    if (!isActive) return;
    if (!token || !isRBD) return;
    if (ranRbdFetch.current) return;
    ranRbdFetch.current = true;
    dispatch(getRBDPerformanceMetrics());
  }, [dispatch, isActive, isRBD, token]);

  useEffect(() => {
    if (!token || isRBD) return;
    if ((role ?? "").toUpperCase() !== "SALESREP") return;
    if (!Array.isArray(repProductGroup) || repProductGroup.length < 1) return;

    if (repProductGroup.length === 1) {
      dispatch(setSelectedProduct(repProductGroup[0]));
    } else if (repProductGroup.length >= 2 && selectedProduct === "") {
      dispatch(setSelectedProduct("All"));
    }

    // Defer KPI calls until the user is actually viewing My Performance.
    if (!isActive) return;
    if (ranSalesRepFetch.current) return;
    ranSalesRepFetch.current = true;

    [
      fetchKpiMyPerformance,
      fetchKpiNationalRegional,
      fetchKpiPendingReferrals,
      fetchKpiHcpGrowers,
      fetchKpiInsights,
      fetchKpiReachFrequency,
      fetchKpiTreatmentRate,
      fetchKpiTopYutrepiaWriters,
      fetchKpiHcpHcoAccountChanges,
      fetchKpiDeNovo,
    ].forEach((thunk) => dispatch(thunk()));
    // Patient / physician insight rows include ProductGroup; filter in UI when the product dropdown changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isActive, token, role, repProductGroup, isRBD]);

  useEffect(() => {
    if (!isActive) return;
    if (!token || !isLeadership) return;
    if (ranLeadershipFetch.current) return;
    ranLeadershipFetch.current = true;
    dispatch(getLeadershipMetrics());
  }, [dispatch, isActive, token, isLeadership]);

  // 6X6 / 3X3 report summary. All three roles now have generated summaries:
  // LEADERSHIP national, RBD region-scoped, SALESREP territory-scoped.
  useEffect(() => {
    if (!isActive) return;
    if (!token) return;
    const roleUpper = (role ?? "").trim().toUpperCase();
    if (!["LEADERSHIP", "RBD", "SALESREP"].includes(roleUpper)) return;
    if (ranReportSummaryFetch.current) return;
    ranReportSummaryFetch.current = true;
    dispatch(fetchKpiReportSummary());
  }, [dispatch, isActive, token, role]);

  const productList = useMemo(() => {
    if (isLeadership) return LEADERSHIP_PRODUCT_LIST;
    const roleKey = (role ?? "").trim().toUpperCase();
    if (roleKey === "SALESREP") {
      return buildProductGroupFilterOptions(repProductGroup);
    }
    return repProductGroup;
  }, [isLeadership, repProductGroup, role]);

  const effectiveSelectedProduct = useMemo(() => {
    if (!productList?.length) return "";
    return productList.includes(selectedProduct)
      ? selectedProduct
      : productList[0];
  }, [productList, selectedProduct]);

  useEffect(() => {
    if (!isActive || !token || effectiveSelectedProduct !== "TYVASO") return;
    const roleKey = (role ?? "").trim().toUpperCase();
    if (roleKey !== "SALESREP" && roleKey !== "RBD") return;
    if (ranTyvasoDpiFetch.current) return;
    ranTyvasoDpiFetch.current = true;
    dispatch(fetchKpiTyvasoDpiDosageDistribution());
    dispatch(fetchKpiTyvasoDpiDosageNotification());
  }, [dispatch, effectiveSelectedProduct, isActive, role, token]);

  const rowsPR = useMemo(() => {
    if (!effectiveSelectedProduct) return [];

    return getPendingReferralsDetailRows(
      kpiPendingReferrals?.data?.pending_referrals_detail,
      effectiveSelectedProduct,
    ).map((item) => ({
      daysOpen:
        item.category === "NA"
          ? "All"
          : item.category.includes("days")
            ? item.category
            : `${item.category} days`,
      noOfReferrals: item.metric_output || 0,
    }));
  }, [kpiPendingReferrals?.data, effectiveSelectedProduct]);

  const rowsHCP = useMemo(() => {
    return getPendingReferralsHcpRows(
      kpiPendingReferrals?.data?.hcp_pending_referrals_count,
      effectiveSelectedProduct,
    ).map((item) => ({
      hcpName: item.HCPName,
      pendingReferrals: item.Pending_Referals_Count ?? 0,
    }));
  }, [kpiPendingReferrals?.data, effectiveSelectedProduct]);

  const pendingReferralsTotal = useMemo(() => {
    return getPendingReferralsTotal(
      kpiPendingReferrals?.data?.total_pending_referrals,
      effectiveSelectedProduct,
    );
  }, [kpiPendingReferrals?.data, effectiveSelectedProduct]);

  useEffect(() => {
    if (!isRBD) return;
    if (
      effectiveSelectedProduct &&
      effectiveSelectedProduct !== selectedProduct
    ) {
      dispatch(setSelectedProduct(effectiveSelectedProduct));
    }
  }, [isRBD, effectiveSelectedProduct, selectedProduct, dispatch]);

  const isDropdownDisabled =
    isLeadership ? false : (repProductGroup?.length || 0) <= 1;

  const rbdKpi = useMemo(() => {
    if (!isRBD) return null;
    const map = rbdMetrics?.kpi_data || {};
    return map[effectiveSelectedProduct] || null;
  }, [isRBD, rbdMetrics, effectiveSelectedProduct]);

  // RBD National / Regional Performance tiles — same row shape as the sales rep
  // tiles (TerritoryDisplayname / PROG_TO_GOAL / ColorBanding / TerritoryFlag),
  // keyed by product group in the /rbd_kpi payload.
  const rbdNationalPerformanceRows = useMemo(() => {
    if (!isRBD) return [];
    return getSalesRepPerformanceRowsByProduct(
      rbdMetrics?.national_performance,
      effectiveSelectedProduct,
    );
  }, [isRBD, rbdMetrics, effectiveSelectedProduct]);

  const rbdRegionalPerformanceRows = useMemo(() => {
    if (!isRBD) return [];
    return getSalesRepPerformanceRowsByProduct(
      rbdMetrics?.regional_performance,
      effectiveSelectedProduct,
    );
  }, [isRBD, rbdMetrics, effectiveSelectedProduct]);

  const salesRepProductMetrics = useMemo(() => {
    return getSalesRepProductMetricsFromApi(
      salesrepMetrics?.productgroup,
      effectiveSelectedProduct,
    );
  }, [salesrepMetrics, effectiveSelectedProduct, repProductGroup]);

  const handleProductsChange = useCallback(
    (nextProduct) => {
      if (!nextProduct || nextProduct === selectedProduct) return;
      if (productList.length && !productList.includes(nextProduct)) return;
      dispatch(setSelectedProduct(nextProduct));
    },
    [dispatch, selectedProduct, productList],
  );

  const insightCounts = useMemo(() => {
    const list = salesrepMetrics?.insightcategory_hcp_counts || [];
    const getCount = (category) =>
      list.find(
        (item) =>
          (item.InsightCategory || "").toLowerCase() === category.toLowerCase(),
      )?.distinct_HCP_count || 0;

    const patientFiltered = (salesrepMetrics?.patient_trends || []).filter(
      (row) =>
        insightRowMatchesSelectedProduct(
          row,
          effectiveSelectedProduct,
          repProductGroup,
        ),
    );
    const physicianFiltered = (
      salesrepMetrics?.physician_behavior || []
    ).filter((row) =>
      insightRowMatchesSelectedProduct(
        row,
        effectiveSelectedProduct,
        repProductGroup,
      ),
    );

    return {
      patientTrend: distinctInsightDetailsCount(patientFiltered),
      utEngagement: getCount("UT Engagement"),
      competitorDynamics: getCount("Competitor Dynamics"),
      physicianBehavior: distinctInsightDetailsCount(physicianFiltered),
    };
  }, [salesrepMetrics, effectiveSelectedProduct, repProductGroup]);

  /**
   * @param {Array} rows
   * @param {{ patientTrends?: boolean }} [opts] Pass `{ patientTrends: true }` for `patient_trends`
   *   rows (`Account`, `AccountFlag`; HCO display names omit Dr. prefix).
   */
  const normalizeInsightRows = useCallback((rows = [], opts = {}) => {
    const isPatientTrend = Boolean(opts?.patientTrends);
    if (!Array.isArray(rows)) return [];
    return rows.map((item) => {
      const rawName = isPatientTrend
        ? item?.Account ??
          item?.AccountName ??
          item?.HCP_Name ??
          item?.HCPName ??
          item?.PhysicianName ??
          ""
        : item?.AccountName ||
          item?.HCP_Name ||
          item?.HCPName ||
          item?.PhysicianName ||
          "";
      const accountFlag = isPatientTrend
        ? (item?.AccountFlag ?? "").toString().trim().toUpperCase()
        : "";
      const trimmed = rawName.toString().trim();
      const name =
        isPatientTrend && accountFlag === "HCO"
          ? trimmed
          : withDoctorNamePrefix(trimmed);
      const base = {
        name,
        details: item?.Details || "",
        date: item?.Date || "",
      };
      return isPatientTrend ? { ...base, accountFlag } : base;
    });
  }, []);

  const patientTrendInsights = useMemo(() => {
    const raw = salesrepMetrics?.patient_trends || [];
    const filtered = raw.filter((row) =>
      insightRowMatchesSelectedProduct(
        row,
        effectiveSelectedProduct,
        repProductGroup,
      ),
    );
    return normalizeInsightRows(filtered, { patientTrends: true });
  }, [
    normalizeInsightRows,
    salesrepMetrics?.patient_trends,
    effectiveSelectedProduct,
    repProductGroup,
  ]);

  const physicianBehaviorInsights = useMemo(() => {
    const raw = salesrepMetrics?.physician_behavior || [];
    const filtered = raw.filter((row) =>
      insightRowMatchesSelectedProduct(
        row,
        effectiveSelectedProduct,
        repProductGroup,
      ),
    );
    return normalizeInsightRows(filtered);
  }, [
    normalizeInsightRows,
    salesrepMetrics?.physician_behavior,
    effectiveSelectedProduct,
    repProductGroup,
  ]);

  const utEngagementInsights = useMemo(
    () => normalizeInsightRows(salesrepMetrics?.ut_engagement),
    [normalizeInsightRows, salesrepMetrics?.ut_engagement],
  );

  const competitorDynamicsInsights = useMemo(
    () => normalizeInsightRows(salesrepMetrics?.competitor_dynamics),
    [normalizeInsightRows, salesrepMetrics?.competitor_dynamics],
  );

  const goToChatWithPrompt = useCallback(
    (prompt) => {
      const params = new URLSearchParams(locationSearchRef.current);
      params.set("tab", "repassist");
      params.set("prefillKey", String(Date.now()));
      navigate(
        { pathname: "/", search: params.toString() },
        {
          replace: false,
          state: {
            prefillPrompt: prompt,
            prefillId: Date.now(),
          },
        },
      );
    },
    [navigate],
  );

  /** Same chat prefill as HCP growers/decliners explore (role-specific wording). */
  const buildHcpGrowersExplorePrompt = useCallback(
    (hcpName) => {
      const r = (role ?? "").trim().toUpperCase();
      if (r === "RBD") return `Help me prep for a call with ${hcpName}`;
      if (r === "LEADERSHIP") return `Give me a summary of ${hcpName}`;
      return `Help me prep for my call with ${hcpName}`;
    },
    [role],
  );

  const hcpNameFromInsightExploreArg = useCallback((maybeRow) => {
    if (!maybeRow || typeof maybeRow !== "object") return "";
    if (
      typeof maybeRow.preventDefault === "function" &&
      "nativeEvent" in maybeRow
    ) {
      return "";
    }
    return maybeRow.name ?? "";
  }, []);

  const handleExplorePatientTrend = useCallback(
    (insightRow) => {
      const name = hcpNameFromInsightExploreArg(insightRow).trim();
      if (!name) {
        const p = (effectiveSelectedProduct || "").trim();
        const productPart =
          p && p !== "All"
            ? ` for ${p}`
            : "";
        goToChatWithPrompt(
          `Summarize the patient trend insights in my territory${productPart}, flag which accounts need the most urgent follow-up, and suggest concrete next steps for this week.`,
        );
        return;
      }
      const flag = (insightRow?.accountFlag ?? "")
        .toString()
        .trim()
        .toUpperCase();
      if (flag === "HCO") {
        goToChatWithPrompt(`Help me prep for a visit to ${name}`);
        return;
      }
      goToChatWithPrompt(buildHcpGrowersExplorePrompt(name));
    },
    [
      goToChatWithPrompt,
      hcpNameFromInsightExploreArg,
      buildHcpGrowersExplorePrompt,
      effectiveSelectedProduct,
    ],
  );

  const handleExploreUtEngagement = useCallback(
    (insightRow) => {
      goToChatWithPrompt(
        buildHcpGrowersExplorePrompt(hcpNameFromInsightExploreArg(insightRow)),
      );
    },
    [
      goToChatWithPrompt,
      buildHcpGrowersExplorePrompt,
      hcpNameFromInsightExploreArg,
    ],
  );

  const handleExploreCompetitorDynamics = useCallback(
    (insightRow) => {
      goToChatWithPrompt(
        buildHcpGrowersExplorePrompt(hcpNameFromInsightExploreArg(insightRow)),
      );
    },
    [
      goToChatWithPrompt,
      buildHcpGrowersExplorePrompt,
      hcpNameFromInsightExploreArg,
    ],
  );

  const handleExplorePhysicianBehavior = useCallback(
    (insightRow) => {
      goToChatWithPrompt(
        buildHcpGrowersExplorePrompt(hcpNameFromInsightExploreArg(insightRow)),
      );
    },
    [
      goToChatWithPrompt,
      buildHcpGrowersExplorePrompt,
      hcpNameFromInsightExploreArg,
    ],
  );

  const territoryOnTrack = useMemo(() => {
    const list =
      rbdMetrics?.rep_track_status?.["On Track"]?.[effectiveSelectedProduct] ||
      [];

    const mapped = list.map((item, idx) => ({
      id: idx + 1,
      name: item.Rep_Name,
      percent: item.PROG_TO_GOAL ?? 0,
    }));

    return mapped.sort((a, b) => (b.percent || 0) - (a.percent || 0));
  }, [rbdMetrics, effectiveSelectedProduct]);

  const territoryNotOnTrack = useMemo(() => {
    const list =
      rbdMetrics?.rep_track_status?.["Not On Track"]?.[
        effectiveSelectedProduct
      ] || [];

    const mapped = list.map((item, idx) => ({
      id: idx + 101,
      name: item.Rep_Name,
      percent: item.PROG_TO_GOAL ?? 0,
    }));

    return mapped.sort((a, b) => (a.percent || 0) - (b.percent || 0));
  }, [rbdMetrics, effectiveSelectedProduct]);

  const overallOnTrackPercent =
    rbdMetrics?.overall_progress?.["On Track"]?.[effectiveSelectedProduct] ?? 0;

  const overallNotOnTrackPercent =
    rbdMetrics?.overall_progress?.["Not On Track"]?.[
      effectiveSelectedProduct
    ] ?? 0;

  const rbdHcpIncrease = useMemo(() => {
    if (!isRBD) return [];
    const growersForProduct =
      rbdMetrics?.hcp_growers?.[effectiveSelectedProduct] || {};
    const list = growersForProduct.Increase || [];
    return list.map((item, idx) => ({
      id: `inc-${idx + 1}`,
      delta: item.Change_in_Metric ?? 0,
      name: item.HCP,
      specialty: item.Specialty,
    }));
  }, [isRBD, rbdMetrics, effectiveSelectedProduct]);

  const rbdHcpDecrease = useMemo(() => {
    if (!isRBD) return [];
    const growersForProduct =
      rbdMetrics?.hcp_growers?.[effectiveSelectedProduct] || {};
    const list = growersForProduct.Decrease || [];
    return list.map((item, idx) => ({
      id: `dec-${idx + 1}`,
      delta: -(item.Change_in_Metric ?? 0),
      name: item.HCP,
      specialty: item.Specialty,
    }));
  }, [isRBD, rbdMetrics, effectiveSelectedProduct]);

  const salesRepHcpIncrease = useMemo(() => {
    if ((role ?? "").toUpperCase() !== "SALESREP") return [];

    const growersForProduct = getSalesRepHcpGrowers(
      salesrepMetrics?.hcp_growers,
      effectiveSelectedProduct,
    );
    const list = growersForProduct.Increase || [];

    return list.map((item, idx) => ({
      id: `inc-${idx + 1}`,
      delta: item.Change_in_Metric ?? 0,
      name: item.HCP,
      specialty: item.Specialty,
    }));
  }, [role, salesrepMetrics, effectiveSelectedProduct]);

  const salesRepHcpDecrease = useMemo(() => {
    if ((role ?? "").toUpperCase() !== "SALESREP") return [];

    const growersForProduct = getSalesRepHcpGrowers(
      salesrepMetrics?.hcp_growers,
      effectiveSelectedProduct,
    );
    const list = growersForProduct.Decrease || [];

    return list.map((item, idx) => ({
      id: `dec-${idx + 1}`,
      delta: -(item.Change_in_Metric ?? 0),
      name: item.HCP,
      specialty: item.Specialty,
    }));
  }, [role, salesrepMetrics, effectiveSelectedProduct]);

  const handleHcpGrowerExploreForSalesRep = useCallback(
    (hcpRow) => {
      goToChatWithPrompt(
        buildHcpGrowersExplorePrompt(hcpRow?.name ?? ""),
      );
    },
    [goToChatWithPrompt, buildHcpGrowersExplorePrompt],
  );

  const handleDeNovoExplore = useCallback(
    (item) => {
      const hcpRaw =
        extractHcpNameFromDeNovoDescription(item?.description) ||
        extractHcpNameFromDeNovoDescription(item?.raw?.Description);
      goToChatWithPrompt(
        buildHcpGrowersExplorePrompt(withDoctorNamePrefix(hcpRaw)),
      );
    },
    [goToChatWithPrompt, buildHcpGrowersExplorePrompt],
  );

  const handleHcpGrowerExploreForRbd = useCallback(
    (hcpRow) => {
      goToChatWithPrompt(
        buildHcpGrowersExplorePrompt(hcpRow?.name ?? ""),
      );
    },
    [goToChatWithPrompt, buildHcpGrowersExplorePrompt],
  );

  const leadershipSelection =
    PRODUCT_SALES_GROUP_MAP[effectiveSelectedProduct] || null;

  const selectedMedicineLeadership = getSelectedMedicineLeadership(
    effectiveSelectedProduct,
  );

  const selectedSalesGroupLeadership = getLeadershipSalesGroup(
    effectiveSelectedProduct,
  );

  const leadershipProductMetrics = useMemo(() => {
    if (!leadershipMetrics) return null;

    if (effectiveSelectedProduct === "All") {
      const allSummary = leadershipMetrics.all_filter_data?.summary_metrics;
      if (!allSummary) return null;

      return {
        goal: allSummary.Referral_Goals ?? 0,
        referrals: allSummary.REFERRALS ?? 0,
        benchmark: allSummary.Benchmark ?? 0,
        referralRequiredToHitGoal:
          allSummary.Referral_required_to_hit_goal ?? 0,
      };
    }

    if (!leadershipSelection) return null;
    const { product } = leadershipSelection;
    const lm = leadershipMetrics.leadership_metrics?.[product];
    if (!lm) return null;

    return {
      goal: lm.Referral_Goals ?? 0,
      referrals: lm.REFERRALS ?? 0,
      benchmark: lm.Benchmark ?? 0,
      referralRequiredToHitGoal: lm.Referral_required_to_hit_goal ?? 0,
    };
  }, [leadershipMetrics, effectiveSelectedProduct, leadershipSelection]);

  const leadershipAlerts = useMemo(() => {
    if (!leadershipMetrics) return null;
    return leadershipMetrics.leadership_alerts || {};
  }, [leadershipMetrics]);

  const leadershipRegionsForCard = useMemo(() => {
    if (!leadershipMetrics) return [];

    if (effectiveSelectedProduct === "All") {
      const regionProgress =
        leadershipMetrics.all_filter_data?.region_progress || [];

      const byRegion = regionProgress.reduce((acc, item) => {
        const region = item.Region_SALES_GROUP;
        if (!acc[region]) {
          acc[region] = { total: 0, count: 0, sample: item };
        }
        acc[region].total += item.PROG_TO_GOAL ?? 0;
        acc[region].count += 1;
        return acc;
      }, {});

      const list = Object.entries(byRegion).map(([regionName, data], idx) => ({
        id: `${regionName}-${idx + 1}`,
        name: regionName,
        percent: Math.round(data.count ? data.total / data.count : 0),
        category: data.sample?.Category || "",
        raw: data.sample,
      }));

      return list.sort((a, b) => (b.percent || 0) - (a.percent || 0));
    }

    if (!leadershipSelection) return [];
    const { product, salesGroup } = leadershipSelection;

    const ranking = leadershipMetrics.regional_peer_ranking || {};
    const regionNames = Object.keys(ranking || {});
    const normalize = (v) => (v ?? "").toString().trim().toUpperCase();
    const salesGroupKey = normalize(salesGroup);

    const list = regionNames
      .map((regionName, idx) => {
        const productArray = ranking[regionName]?.[product] || [];
        const match = salesGroupKey
          ? productArray.find(
              (x) => normalize(x.SALES_GROUP) === salesGroupKey,
            )
          : productArray[0];

        if (!match) return null;

        const percent = Math.round(match.PROG_TO_GOAL ?? 0);

        return {
          id: `${regionName}-${idx + 1}`,
          name: regionName,
          percent,
          category: match?.Category || "",
          raw: match,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.percent || 0) - (a.percent || 0));

    return list;
  }, [leadershipMetrics, effectiveSelectedProduct, leadershipSelection]);

  const leadershipHcpGrowers = useMemo(() => {
    if (!leadershipMetrics) {
      return { increase: [], decrease: [] };
    }

    if (effectiveSelectedProduct === "All") {
      const allHcp = leadershipMetrics.all_filter_data?.hcp_metrics || {};
      const incList = allHcp.Increase || [];
      const decList = allHcp.Decrease || [];

      const increase = incList.map((item, idx) => ({
        id: `inc-${idx + 1}`,
        delta: item.Change_in_Metric ?? 0,
        name: item.HCP,
        specialty: item.Specialty,
      }));

      const decrease = decList.map((item, idx) => ({
        id: `dec-${idx + 1}`,
        delta: -(item.Change_in_Metric ?? 0),
        name: item.HCP,
        specialty: item.Specialty,
      }));

      return { increase, decrease };
    }

    if (!leadershipSelection) return { increase: [], decrease: [] };

    const { product, salesGroup } = leadershipSelection;
    const growersByProduct = leadershipMetrics.hcp_key_metrics?.[product] || {};

    const normalize = (v) => (v ?? "").toString().trim().toUpperCase();

    const shouldFilterBySalesGroup =
      !!salesGroup && normalize(salesGroup) !== "ALL";

    const incListRaw = growersByProduct.Increase || [];
    const decListRaw = growersByProduct.Decrease || [];

    const incList = shouldFilterBySalesGroup
      ? incListRaw.filter(
          (x) => normalize(x.SALES_GROUP) === normalize(salesGroup),
        )
      : incListRaw;

    const decList = shouldFilterBySalesGroup
      ? decListRaw.filter(
          (x) => normalize(x.SALES_GROUP) === normalize(salesGroup),
        )
      : decListRaw;

    const increase = incList.map((item, idx) => ({
      id: `inc-${idx + 1}`,
      delta: item.Change_in_Metric ?? 0,
      name: item.HCP,
      specialty: item.Specialty,
    }));

    const decrease = decList.map((item, idx) => ({
      id: `dec-${idx + 1}`,
      delta: -(item.Change_in_Metric ?? 0),
      name: item.HCP,
      specialty: item.Specialty,
    }));

    return { increase, decrease };
  }, [leadershipMetrics, effectiveSelectedProduct, leadershipSelection]);

  const handleHcpGrowerExploreForLeadership = useCallback(
    (hcpRow) => {
      goToChatWithPrompt(
        buildHcpGrowersExplorePrompt(hcpRow?.name ?? ""),
      );
    },
    [goToChatWithPrompt, buildHcpGrowersExplorePrompt],
  );

  const leadershipAlertsForUI = useMemo(
    () => leadershipAlerts || {},
    [leadershipAlerts],
  );

  const roleKey = (role || "").toUpperCase();

  const refreshTimestamp = useMemo(() => {
    if (roleKey === "SALESREP") {
      return kpiMyPerformance?.data?.refresh_timestamp ?? "";
    }
    if (roleKey === "RBD") {
      return rbdMetrics?.refresh_timestamp ?? "";
    }
    if (roleKey === "LEADERSHIP") {
      return leadershipMetrics?.refresh_timestamp ?? "";
    }
    return "";
  }, [roleKey, kpiMyPerformance?.data, rbdMetrics, leadershipMetrics]);

  const noGoalsDataAvailable = useMemo(() => {
    if (roleKey === "SALESREP") {
      return !salesrepMetrics?.latest_goals_data_available_flag;
    }

    if (roleKey === "RBD") {
      return !rbdMetrics?.latest_goals_data_available_flag;
    }

    if (roleKey === "LEADERSHIP") {
      return !leadershipMetrics?.latest_goals_data_available_flag;
    }

    return false;
  }, [roleKey, salesrepMetrics, rbdMetrics, leadershipMetrics]);

  const goalsDataDisclaimerShow = useMemo(() => {
    console.log("goalsDataDisclaimerShow", salesrepMetrics);
    if (roleKey === "SALESREP") return !!salesrepMetrics?.goals_data_disclaimer_show;
    if (roleKey === "RBD") return !!rbdMetrics?.goals_data_disclaimer_show;
    if (roleKey === "LEADERSHIP") return !!leadershipMetrics?.goals_data_disclaimer_show;
    return false;
  }, [roleKey, salesrepMetrics, rbdMetrics, leadershipMetrics]);

  const hcpGrowersLoading = useMemo(() => {
    if (roleKey === "SALESREP") return kpiHcpGrowers.isLoading;
    if (roleKey === "RBD") return isRBDLoading;
    if (roleKey === "LEADERSHIP") return isLeadershipLoading;
    return false;
  }, [roleKey, kpiHcpGrowers.isLoading, isRBDLoading, isLeadershipLoading]);

  const treatmentRateSource = useMemo(() => {
    if (roleKey === "RBD") {
      return {
        rows: rbdMetrics?.treatment_rate,
        loading: isRBDLoading,
        error: isRBDError,
      };
    }
    if (roleKey === "LEADERSHIP") {
      return {
        rows: leadershipMetrics?.treatment_rate,
        loading: isLeadershipLoading,
        error: isLeadershipError,
      };
    }
    return {
      rows: kpiTreatmentRate?.data?.treatment_rate,
      loading: kpiTreatmentRate.isLoading,
      error: kpiTreatmentRate.isError,
    };
  }, [
    roleKey,
    rbdMetrics?.treatment_rate,
    isRBDLoading,
    isRBDError,
    leadershipMetrics?.treatment_rate,
    isLeadershipLoading,
    isLeadershipError,
    kpiTreatmentRate?.data?.treatment_rate,
    kpiTreatmentRate.isLoading,
    kpiTreatmentRate.isError,
  ]);

  /** De novo: salesrep uses /kpi_metrics/denovo; RBD/Leadership use bundled KPI payloads. */
  const deNovoSource = useMemo(() => {
    if (roleKey === "RBD") {
      return {
        data: rbdMetrics?.de_novo ?? null,
        loading: isRBDLoading,
      };
    }
    if (roleKey === "LEADERSHIP") {
      return {
        data: leadershipMetrics?.de_novo ?? null,
        loading: isLeadershipLoading,
      };
    }
    return {
      data: kpiDeNovo?.data ?? null,
      loading: kpiDeNovo.isLoading,
    };
  }, [
    roleKey,
    rbdMetrics?.de_novo,
    isRBDLoading,
    leadershipMetrics?.de_novo,
    isLeadershipLoading,
    kpiDeNovo?.data,
    kpiDeNovo.isLoading,
  ]);

  const widgetProps = useMemo(
    () =>
      buildMyPerformanceWidgetProps({
        roleKey,
        salesRepProductMetrics,
        productList,
        effectiveSelectedProduct,
        handleProductsChange,
        isDropdownDisabled,
        locationSearch: getLocationSearch,
        navigate,
        salesrepMetrics,
        rowsPR,
        rowsHCP,
        pendingReferralsTotal,
        insightCounts,
        handleExplorePatientTrend,
        handleExploreUtEngagement,
        handleExploreCompetitorDynamics,
        handleExplorePhysicianBehavior,
        insightsLoading: kpiInsights.isLoading,
        patientTrendInsights,
        physicianBehaviorInsights,
        utEngagementInsights,
        competitorDynamicsInsights,
        salesRepHcpIncrease,
        salesRepHcpDecrease,
        handleHcpGrowerExploreForSalesRep,
        handleDeNovoExplore,
        rbdKpi,
        rbdNationalPerformanceRows,
        rbdRegionalPerformanceRows,
        territoryOnTrack,
        territoryNotOnTrack,
        overallOnTrackPercent,
        overallNotOnTrackPercent,
        handleTerritoryExploreClick,
        rbdHcpIncrease,
        rbdHcpDecrease,
        handleHcpGrowerExploreForRbd,
        leadershipProductMetrics,
        selectedMedicineLeadership,
        leadershipAlertsForUI,
        leadershipSelection,
        selectedSalesGroupLeadership,
        goToChatWithPrompt,
        leadershipRegionsForCard,
        leadershipHcpGrowers,
        handleHcpGrowerExploreForLeadership,
        noGoalsDataAvailable,
        goalsDataDisclaimerShow,
        rbdSummaryLoading: isRBDLoading,
        salesRepMyPerformanceLoading: kpiMyPerformance.isLoading,
        salesRepNationalRegionalLoading: kpiNationalRegional.isLoading,
        salesRepPendingReferralsLoading: kpiPendingReferrals.isLoading,
        salesRepReachFrequencyLoading: kpiReachFrequency.isLoading,
        treatmentRateRows: treatmentRateSource.rows,
        treatmentRateLoading: treatmentRateSource.loading,
        treatmentRateError: treatmentRateSource.error,
        salesRepTopYutrepiaWritersLoading: kpiTopYutrepiaWriters.isLoading,
        salesRepHcpHcoAccountChangesLoading: kpiHcpHcoAccountChanges.isLoading,
        deNovoData: deNovoSource.data,
        deNovoLoading: deNovoSource.loading,
        tyvasoDpiDosageDistributionData:
          kpiTyvasoDpiDosageDistribution?.data,
        tyvasoDpiDosageDistributionLoading:
          kpiTyvasoDpiDosageDistribution.isLoading,
        tyvasoDpiDosageDistributionError:
          kpiTyvasoDpiDosageDistribution.isError,
        tyvasoDpiDosageNotificationData:
          kpiTyvasoDpiDosageNotification?.data,
        tyvasoDpiDosageNotificationLoading:
          kpiTyvasoDpiDosageNotification.isLoading,
        reportSummaryData: kpiReportSummary?.data,
        reportSummaryLoading: kpiReportSummary?.isLoading,
        reportSummaryError: kpiReportSummary?.isError,
        tyvasoDpiDosageNotificationError:
          kpiTyvasoDpiDosageNotification.isError,
        hcpGrowersLoading,
      }),
    [
      roleKey,
      kpiMyPerformance.isLoading,
      kpiNationalRegional.isLoading,
      kpiPendingReferrals.isLoading,
      kpiReachFrequency.isLoading,
      treatmentRateSource,
      kpiTopYutrepiaWriters.isLoading,
      kpiHcpHcoAccountChanges.isLoading,
      deNovoSource,
      kpiTyvasoDpiDosageDistribution?.data,
      kpiTyvasoDpiDosageDistribution.isLoading,
      kpiTyvasoDpiDosageDistribution.isError,
      kpiTyvasoDpiDosageNotification?.data,
      kpiTyvasoDpiDosageNotification.isLoading,
      kpiTyvasoDpiDosageNotification.isError,
      kpiReportSummary?.data,
      kpiReportSummary?.isLoading,
      kpiReportSummary?.isError,
      hcpGrowersLoading,
      salesRepProductMetrics,
      productList,
      effectiveSelectedProduct,
      handleProductsChange,
      isDropdownDisabled,
      repProductGroup,
      getLocationSearch,
      navigate,
      salesrepMetrics,
      rowsPR,
      rowsHCP,
      pendingReferralsTotal,
      insightCounts,
      handleExplorePatientTrend,
      handleExploreUtEngagement,
      handleExploreCompetitorDynamics,
      handleExplorePhysicianBehavior,
      kpiInsights.isLoading,
      patientTrendInsights,
      physicianBehaviorInsights,
      utEngagementInsights,
      competitorDynamicsInsights,
      salesRepHcpIncrease,
      salesRepHcpDecrease,
      handleHcpGrowerExploreForSalesRep,
      handleDeNovoExplore,
      rbdKpi,
      territoryOnTrack,
      territoryNotOnTrack,
      overallOnTrackPercent,
      overallNotOnTrackPercent,
      handleTerritoryExploreClick,
      rbdNationalPerformanceRows,
      rbdRegionalPerformanceRows,
      rbdHcpIncrease,
      rbdHcpDecrease,
      handleHcpGrowerExploreForRbd,
      leadershipProductMetrics,
      selectedMedicineLeadership,
      leadershipAlertsForUI,
      leadershipSelection,
      selectedSalesGroupLeadership,
      goToChatWithPrompt,
      leadershipRegionsForCard,
      leadershipHcpGrowers,
      handleHcpGrowerExploreForLeadership,
      noGoalsDataAvailable,
      goalsDataDisclaimerShow,
      isRBDLoading,
    ],
  );

  const isRepInfoReady =
    Array.isArray(repProductGroup) && repProductGroup.length > 0;
  /** Sales rep: only block the page while rep/product context loads; KPIs use per-card loaders. */
  const showLoader =
    (roleKey === "SALESREP"
      ? isRepLoading || !isRepInfoReady
      : !isRBD &&
        !isLeadership &&
        (isRepLoading || !isRepInfoReady)) ||
    isRBDLoading ||
    (isLeadership && isLeadershipLoading);

  return {
    showLoader,
    widgetProps,
    headerProps: {
      options: productList,
      value: effectiveSelectedProduct,
      onChange: handleProductsChange,
      disabled: isDropdownDisabled,
      refreshTimestamp,
      ...(roleKey === "SALESREP"
        ? { repGreetingFirstName: displayFirstName }
        : {}),
    },
    isLeadership,
    firstName: displayFirstName,
    displayIdentityLoading,
    roleKey,
    effectiveSelectedProduct,
    onExploreHcpClick: handleExploreClick,
  };
}
